import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Upload, AlertTriangle, CheckSquare, Square } from 'lucide-react';
import { getGenresForBookType, getThemesForGenre } from '@/constants/genreThemes';

/**
 * SimpleIngestGate1Form - Shared Gate 1 Metadata Form
 * 
 * This is the SINGLE SOURCE OF TRUTH for the manuscript upload form.
 * Used by both:
 * - SimpleIngestConsole (developer debug console)
 * - StreamlinedUploadFlow (production author upload)
 * 
 * Props:
 * - isOpen: boolean - whether the form dialog is open
 * - onClose: () => void - callback when form is closed/cancelled
 * - onSubmit: (formData: FormData) => void - callback with form data when submitted
 * - isSubmitting: boolean - whether submission is in progress
 * - isModal: boolean - whether to render as modal (default true) or inline
 */
const SimpleIngestGate1Form = ({ 
  isOpen = true,
  onClose, 
  onSubmit, 
  isSubmitting = false,
  isModal = true,
  // Publisher managed author props
  publisherAuthorName = null  // If set, use this instead of logged-in user's name
}) => {
  const { user } = useAuth();
  
  // Determine the author name to display/submit
  // Priority: publisherAuthorName (managed author) > user.name > user.email
  const authorDisplayName = publisherAuthorName || user?.name || user?.email || 'Unknown Author';
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    isbn: '',
    book_type: 'fiction',
    genre: '',
    theme: '',
    purchase_url: '',
    is_promotional: false,
    description: '',
    coverFile: null,
    epubFile: null,
  });
  
  // Attestation state
  const [attestationAcked, setAttestationAcked] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Reset dependent fields when parent changes
      if (field === 'book_type') {
        newData.genre = '';
        newData.theme = '';
      }
      if (field === 'genre') {
        newData.theme = '';
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle file changes
  const handleFileChange = (field, files) => {
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file types
      if (field === 'coverFile') {
        const validTypes = ['.jpg', '.jpeg', '.png', '.webp'];
        const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        if (!validTypes.includes(ext)) {
          setFormErrors(prev => ({ ...prev, cover: 'Invalid file type. Use .jpg, .png, or .webp' }));
          return;
        }
      }
      
      if (field === 'epubFile') {
        if (!file.name.toLowerCase().endsWith('.epub')) {
          setFormErrors(prev => ({ ...prev, epub: 'Invalid file type. Must be .epub' }));
          return;
        }
      }
      
      setFormData(prev => ({ ...prev, [field]: file }));
      
      // Clear error for this field
      if (field === 'coverFile' && formErrors.cover) {
        setFormErrors(prev => ({ ...prev, cover: null }));
      }
      if (field === 'epubFile' && formErrors.epub) {
        setFormErrors(prev => ({ ...prev, epub: null }));
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!formData.coverFile) {
      errors.cover = 'Cover image is required';
    }
    if (!formData.epubFile) {
      errors.epub = 'EPUB file is required';
    }
    if (!attestationAcked) {
      errors.attestation = 'You must acknowledge the rights attestation';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Build FormData exactly as SimpleIngestConsole does
    const formDataObj = new FormData();
    formDataObj.append('manuscript-title', formData.title.trim());
    formDataObj.append('manuscript-subtitle', formData.subtitle.trim() || '');
    formDataObj.append('publishing-company', authorDisplayName);
    formDataObj.append('cover-image', formData.coverFile);
    formDataObj.append('epub', formData.epubFile);
    
    // Add full metadata fields
    formDataObj.append('isbn', formData.isbn.trim() || '');
    formDataObj.append('book_type', formData.book_type);
    formDataObj.append('genre', formData.genre || '');
    formDataObj.append('theme', formData.theme || '');
    formDataObj.append('purchase_url', formData.purchase_url.trim() || '');
    formDataObj.append('is_promotional', formData.is_promotional ? 'true' : 'false');
    formDataObj.append('description', formData.description.trim() || '');
    
    // Call parent's onSubmit with the FormData
    onSubmit(formDataObj);
  };

  // Reset form when closed - using key pattern instead of useEffect setState
  // The form will reset naturally when isOpen changes due to React's reconciliation

  // Form content
  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Book Details Section */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-200 border-b border-slate-700 pb-2">Book Details</h3>
        
        {/* Title */}
        <div>
          <Label htmlFor="manuscript-title" className="text-slate-300">
            Title <span className="text-red-400">*</span>
          </Label>
          <Input
            id="manuscript-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter book title"
            className={`bg-slate-800 border-slate-600 text-white mt-1 ${formErrors.title ? 'border-red-500' : ''}`}
            data-testid="manuscript-title-input"
          />
          {formErrors.title && <p className="text-red-400 text-xs mt-1">{formErrors.title}</p>}
        </div>
        
        {/* Subtitle */}
        <div>
          <Label htmlFor="manuscript-subtitle" className="text-slate-300">Subtitle</Label>
          <Input
            id="manuscript-subtitle"
            value={formData.subtitle}
            onChange={(e) => handleInputChange('subtitle', e.target.value)}
            placeholder="Enter subtitle (optional)"
            className="bg-slate-800 border-slate-600 text-white mt-1"
            data-testid="manuscript-subtitle-input"
          />
        </div>
        
        {/* Author (read-only) */}
        <div>
          <Label className="text-slate-300">Author</Label>
          <Input
            value={authorDisplayName}
            disabled
            className="bg-slate-800/50 border-slate-700 text-slate-400 mt-1"
          />
          <p className="text-xs text-slate-500 mt-1">
            {publisherAuthorName ? 'Managed author pen name' : 'From your account profile'}
          </p>
        </div>
        
        {/* ISBN */}
        <div>
          <Label htmlFor="isbn" className="text-slate-300">ISBN</Label>
          <Input
            id="isbn"
            value={formData.isbn}
            onChange={(e) => handleInputChange('isbn', e.target.value)}
            placeholder="ISBN-10 or ISBN-13 (optional)"
            className="bg-slate-800 border-slate-600 text-white mt-1"
          />
          <p className="text-xs text-slate-500 mt-1">Optional: ISBN for your eBook version</p>
        </div>
        
        {/* Book Type Toggle */}
        <div>
          <Label className="text-slate-300">Book Type</Label>
          <div className="flex gap-2 mt-1">
            <Button
              type="button"
              onClick={() => handleInputChange('book_type', 'fiction')}
              className={`flex-1 ${formData.book_type === 'fiction' ? 'bg-indigo-600' : 'bg-slate-700 hover:bg-slate-600'}`}
            >
              Fiction
            </Button>
            <Button
              type="button"
              onClick={() => {
                handleInputChange('book_type', 'non-fiction');
                handleInputChange('genre', '');
                handleInputChange('theme', '');
              }}
              className={`flex-1 ${formData.book_type === 'non-fiction' ? 'bg-indigo-600' : 'bg-slate-700 hover:bg-slate-600'}`}
            >
              Non-Fiction
            </Button>
          </div>
        </div>
        
        {/* Genre & Theme */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="genre" className="text-slate-300">Genre</Label>
            <select
              id="genre"
              value={formData.genre}
              onChange={(e) => {
                handleInputChange('genre', e.target.value);
                handleInputChange('theme', '');
              }}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white"
            >
              {getGenresForBookType(formData.book_type).map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="theme" className="text-slate-300">Theme</Label>
            <select
              id="theme"
              value={formData.theme}
              onChange={(e) => handleInputChange('theme', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white"
            >
              <option value="">Select theme</option>
              {getThemesForGenre(formData.genre).map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Purchase URL */}
        <div>
          <Label htmlFor="purchase_url" className="text-slate-300">Purchase URL</Label>
          <Input
            id="purchase_url"
            type="url"
            value={formData.purchase_url}
            onChange={(e) => handleInputChange('purchase_url', e.target.value)}
            placeholder="https://example.com/book (optional)"
            className="bg-slate-800 border-slate-600 text-white mt-1"
          />
        </div>
        
        {/* Promotional Checkbox */}
        <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <input
            type="checkbox"
            id="is_promotional"
            checked={formData.is_promotional}
            onChange={(e) => handleInputChange('is_promotional', e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-600"
          />
          <div>
            <Label htmlFor="is_promotional" className="text-slate-200 cursor-pointer">
              Promotional Book (Free to Read)
            </Label>
            <p className="text-xs text-slate-500 mt-0.5">
              Completely free for any NewAudia subscriber. EUs tracked for analytics but never billed.
            </p>
          </div>
        </div>
      </div>
      
      {/* Cover Image Section */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-200 border-b border-slate-700 pb-2">Cover Image</h3>
        <div>
          <Label htmlFor="cover-image" className="text-slate-300">
            Book Cover <span className="text-red-400">*</span>
          </Label>
          <Input
            id="cover-image"
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            onChange={(e) => handleFileChange('coverFile', e.target.files)}
            className={`bg-slate-800 border-slate-600 text-white mt-1 file:bg-slate-700 file:text-white file:border-0 ${formErrors.cover ? 'border-red-500' : ''}`}
            data-testid="cover-image-input"
          />
          <p className="text-xs text-slate-400 mt-1">.jpg, .png, .webp (max 5 MB)</p>
          {formErrors.cover && <p className="text-red-400 text-xs mt-1">{formErrors.cover}</p>}
          {formData.coverFile && (
            <p className="text-green-400 text-xs mt-1">✓ {formData.coverFile.name}</p>
          )}
        </div>
      </div>
      
      {/* Manuscript File Section */}
      <div className="space-y-3">
        <h3 className="font-semibold text-indigo-300 border-b border-indigo-700/50 pb-2">Manuscript File</h3>
        <div>
          <Label htmlFor="epub-file" className="text-slate-300">
            EPUB File <span className="text-red-400">*</span>
          </Label>
          <Input
            id="epub-file"
            type="file"
            accept=".epub"
            onChange={(e) => handleFileChange('epubFile', e.target.files)}
            className={`bg-slate-800 border-slate-600 text-white mt-1 file:bg-slate-700 file:text-white file:border-0 ${formErrors.epub ? 'border-red-500' : ''}`}
            data-testid="epub-file-input"
          />
          <p className="text-xs text-slate-400 mt-1">.epub (max 20 MB)</p>
          {formErrors.epub && <p className="text-red-400 text-xs mt-1">{formErrors.epub}</p>}
          {formData.epubFile && (
            <p className="text-green-400 text-xs mt-1">✓ {formData.epubFile.name}</p>
          )}
        </div>
      </div>
      
      {/* Rights Attestation Section */}
      <div className="space-y-3 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
        <h3 className="font-semibold text-amber-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Rights & Authority Attestation
        </h3>
        <p className="text-amber-200/80 text-sm">
          You must acknowledge this attestation to upload.
        </p>
        <div className="bg-slate-900/50 p-3 rounded-lg text-xs text-slate-300 max-h-32 overflow-y-auto">
          <p className="font-semibold mb-2">Publisher Upload Authorization & Rights Attestation</p>
          <p className="mb-2">
            By proceeding, I confirm that I am authorized to upload, distribute, and manage this work.
          </p>
          <p className="mb-1">I attest that:</p>
          <ol className="list-decimal ml-4 space-y-1">
            <li>I have obtained all necessary rights, licenses, and permissions to distribute this work through NewAudia.</li>
            <li>To the best of my knowledge, this work does not infringe on any third-party intellectual property rights.</li>
            <li>I understand that NewAudia relies on this attestation and I am solely responsible for ensuring the legality of this upload.</li>
            <li>I acknowledge that I am solely responsible for any legal claims arising from the content of this work.</li>
            <li>I understand that NewAudia may remove or disable access to this work if it receives notice of infringement.</li>
          </ol>
        </div>
        <button
          type="button"
          onClick={() => setAttestationAcked(!attestationAcked)}
          className={`flex items-center gap-3 w-full p-3 rounded-lg border transition-colors ${
            attestationAcked 
              ? 'border-emerald-500/50 bg-emerald-900/20' 
              : 'border-slate-600 bg-slate-800 hover:border-slate-500'
          } ${formErrors.attestation ? 'border-red-500' : ''}`}
          data-testid="attestation-checkbox"
        >
          {attestationAcked ? (
            <CheckSquare className="w-5 h-5 text-emerald-400" />
          ) : (
            <Square className="w-5 h-5 text-slate-400" />
          )}
          <span className={attestationAcked ? 'text-emerald-300' : 'text-slate-300'}>
            I acknowledge the rights attestation and confirm authority to upload.
          </span>
        </button>
        {formErrors.attestation && <p className="text-red-400 text-xs">{formErrors.attestation}</p>}
      </div>
      
      <DialogFooter className="gap-2 pt-4 border-t border-slate-700">
        {onClose && (
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="cancel-ingestion-btn"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-indigo-600 hover:bg-indigo-500"
          data-testid="upload-start-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload & Start
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  );

  // Render as modal or inline based on isModal prop
  if (!isModal) {
    return formContent;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-xl">Upload New Manuscript</DialogTitle>
          <DialogDescription className="text-slate-400">
            Complete all required fields to start manuscript ingestion
          </DialogDescription>
        </DialogHeader>
        
        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default SimpleIngestGate1Form;
